/** D-126: the client's sheet — details, consent (law 2024/017), invoices and contracts, with
 * « New invoice » and « New contract » for this client. */

import {
  type BillingInvoicesListParams,
  type ContractsItemsListParams,
  useBillingInvoicesList,
  useContractsItemsList,
  useCrmContactsRetrieve,
  useCrmLeadsList,
} from "@agencyos/api-client";
import { countryName, formatPhone, formatXaf } from "@agencyos/core";
import { Avatar, Badge, Button, EmptyState, GlassCard, Skeleton } from "@agencyos/ui-web";
import { FilePlus2, FileSignature, ReceiptText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Page } from "../components/AppShell";
import { useCurrentRole } from "../lib/settings";
import { InvoiceFormSheet } from "./billing/InvoiceFormSheet";
import { ContactCalls } from "./calls/CallList";
import { dateText, invoiceTone } from "./billing/shared";
import { NewContractSheet } from "./contracts/NewContractSheet";
import { STATUS_TONE, dateOnly } from "./contracts/shared";

function phoneText(phone?: string | null): string {
  if (!phone) return "—";
  try {
    return formatPhone(phone);
  } catch {
    return phone;
  }
}

export function ClientPage() {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const crmRole = useCurrentRole("crm");
  const billingRole = useCurrentRole("billing");
  const contractsRole = useCurrentRole("contracts");
  const canSeeLeads = crmRole === "owner" || crmRole === "manager" || crmRole === "advisor";
  const canSeeInvoices = ["owner", "manager", "advisor", "accountant"].includes(billingRole ?? "");
  const canInvoice = canSeeInvoices;
  const canSeeContracts = ["owner", "manager", "advisor", "accountant"].includes(contractsRole ?? "");
  const canContract = contractsRole === "owner" || contractsRole === "manager" || contractsRole === "advisor";
  const contact = useCrmContactsRetrieve(id);
  const leads = useCrmLeadsList({ contact: id }, { query: { enabled: canSeeLeads } });
  const invoiceParams = { contact: id } as BillingInvoicesListParams;
  const invoices = useBillingInvoicesList(invoiceParams, { query: { enabled: canSeeInvoices } });
  const contractParams: ContractsItemsListParams = { contact: id };
  const contracts = useContractsItemsList(contractParams, { query: { enabled: canSeeContracts } });
  const [invoicing, setInvoicing] = useState(false);
  const [contracting, setContracting] = useState(false);

  if (contact.isPending) {
    return (
      <Page>
        <Skeleton style={{ width: "40%", height: 28 }} />
        <Skeleton style={{ height: 160 }} />
      </Page>
    );
  }
  if (contact.data?.status !== 200) {
    return (
      <Page title={t("clients.title")}>
        <GlassCard role="alert" className="stack">
          <p>{t("clients.notFound")}</p>
          <Link className="link" to="/leads">
            {t("clients.back")}
          </Link>
        </GlassCard>
      </Page>
    );
  }
  const c = contact.data.data;
  const lead = leads.data?.status === 200 ? leads.data.data.results[0] : undefined;
  const invoiceRows = invoices.data?.status === 200 ? invoices.data.data.results : [];
  const contractRows = contracts.data?.status === 200 ? contracts.data.data.results : [];
  const name = c.full_name || phoneText(c.phone_e164);
  return (
    <Page
      title={name}
      description={
        <>
          <Link to="/leads" className="link">
            {t("crm.title")}
          </Link>
          {lead ? <> · {t(`crm.stage.${lead.stage ?? "new"}`)}</> : null}
        </>
      }
      actions={
        <>
          {canInvoice ? (
            <Button icon={<ReceiptText size={16} aria-hidden="true" />} onClick={() => setInvoicing(true)}>
              {t("clients.newInvoice")}
            </Button>
          ) : null}
          {canContract ? (
            <Button variant="secondary" icon={<FilePlus2 size={16} aria-hidden="true" />} onClick={() => setContracting(true)}>
              {t("clients.newContract")}
            </Button>
          ) : null}
        </>
      }
    >
      <GlassCard as="section" className="stack" aria-label={t("clients.details")}>
        <div className="lead-head">
          <Avatar name={name} size="lg" />
          <div className="lead-head__who">
            <h2>{name}</h2>
            <p className="muted ao-num">{phoneText(c.phone_e164)}</p>
          </div>
          {lead?.stage === "client" ? <Badge dot tone="success">{t("crm.stage.client")}</Badge> : null}
        </div>
        <dl className="client-facts">
          <div>
            <dt>{t("clients.fields.email")}</dt>
            <dd>{c.email || "—"}</dd>
          </div>
          <div>
            <dt>{t("clients.fields.country")}</dt>
            <dd>{c.country ? countryName(c.country, i18n.language) : "—"}</dd>
          </div>
          <div>
            <dt>{t("clients.fields.city")}</dt>
            <dd>{c.city || "—"}</dd>
          </div>
          <div>
            <dt>{t("clients.fields.language")}</dt>
            <dd>{t(`clients.language.${c.language ?? "fr"}`)}</dd>
          </div>
          {lead ? (
            <>
              <div>
                <dt>{t("crm.columns.source")}</dt>
                <dd>{t(`crm.source.${lead.source ?? "other"}`)}</dd>
              </div>
              <div>
                <dt>{t("crm.columns.owner")}</dt>
                <dd>{lead.assigned_to_name || t("crm.unassigned")}</dd>
              </div>
              {lead.service_code ? (
                <div>
                  <dt>{t("crm.columns.service")}</dt>
                  <dd>
                    <span className="code-chip">{lead.service_code}</span>
                  </dd>
                </div>
              ) : null}
            </>
          ) : null}
          <div>
            <dt>{t("clients.fields.consent")}</dt>
            <dd>
              {c.consent_at
                ? t("clients.consentOn", {
                    date: dateText(c.consent_at, i18n.language),
                    source: c.consent_source ? t(`clients.consentSource.${c.consent_source}`) : "—",
                  })
                : t("clients.noConsent")}
            </dd>
          </div>
        </dl>
      </GlassCard>
      {canSeeLeads ? <ContactCalls contactId={c.id} /> : null}
      {canSeeInvoices ? (
        <GlassCard as="section" className="stack">
          <h2>{t("clients.invoices")}</h2>
          {invoices.isPending ? <Skeleton style={{ height: 60 }} /> : null}
          {invoices.data?.status === 200 && !invoiceRows.length ? <p className="muted">{t("billing.noInvoice")}</p> : null}
          {invoiceRows.length ? (
            <ul className="plain-list stack">
              {invoiceRows.map((invoice) => (
                <li key={invoice.id} className="row between">
                  <Link className="link" to={`/invoices/${invoice.id}`}>
                    {t(`billing.kind.${invoice.kind}`)} {invoice.number || t("billing.draftNumber")}
                  </Link>
                  <span className="row">
                    <span className="ao-num">{formatXaf(invoice.total_ttc_xaf)}</span>
                    <Badge tone={invoiceTone(invoice.status)}>{t(`billing.status.${invoice.status}`)}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </GlassCard>
      ) : null}
      {canSeeContracts ? (
        <GlassCard as="section" className="stack">
          <h2>{t("clients.contracts")}</h2>
          {contracts.isPending ? <Skeleton style={{ height: 60 }} /> : null}
          {contracts.data?.status === 200 && !contractRows.length ? (
            <EmptyState icon={<FileSignature size={22} strokeWidth={1.75} />} title={t("contracts.list.empty")} />
          ) : null}
          {contractRows.length ? (
            <ul className="plain-list stack">
              {contractRows.map((contract) => (
                <li key={contract.id} className="row between">
                  <Link className="link" to={`/contracts/${contract.id}`}>
                    {contract.number || t("contracts.draftNumber")} · <span className="code-chip">{contract.package_code}</span>
                  </Link>
                  <span className="row">
                    <span className="muted">{dateOnly(contract.created_at, i18n.language)}</span>
                    <Badge dot tone={STATUS_TONE[contract.status]}>{t(`contracts.status.${contract.status}`)}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </GlassCard>
      ) : null}
      {invoicing ? <InvoiceFormSheet open contactId={c.id} contactName={name} onClose={() => setInvoicing(false)} /> : null}
      {contracting ? (
        <NewContractSheet open contactId={c.id} contactName={name} contactLanguage={c.language} onClose={() => setContracting(false)} />
      ) : null}
    </Page>
  );
}
